/**
 * Readiness display + filter logic (SYN-1414, Command UI).
 *
 * Command consumes Atlas's DERIVED readiness (SYN-1411). Per ACSS 1.3 discovery
 * is not readiness — so this module enforces a render-boundary guard:
 * `normalizeState` fail-closes any unrecognized value to `unknown` (never
 * `ready`), and `primaryAction` only offers "Open" for a genuinely `ready`
 * snapshot. Defense in depth: the backend wire parser already does this, and we
 * repeat it here so the guard holds regardless of upstream changes.
 */

import {
  displayName,
  readinessReasonLabel,
  readinessStateCopy,
  setupActionCopy,
  systemNameFromRef,
} from "./buyer-language";

export type ReadinessState =
  | "ready"
  | "blocked"
  | "needs_setup"
  | "unavailable"
  | "stale"
  | "unknown";

export const ALL_STATES: readonly ReadinessState[] = [
  "ready",
  "needs_setup",
  "blocked",
  "stale",
  "unavailable",
  "unknown",
] as const;

const STATE_SET = new Set<ReadinessState>(ALL_STATES);

export interface ReadinessEvidence {
  kind: string;
  status: string;
  requirementKey?: string;
  capabilityId?: string;
  subjectRef?: string;
  source: string;
  sourceRef?: string;
  detail?: string;
  observedAt?: string;
  /**
   * Forward-compatible per-evidence detail bag. Atlas may carry connector-level
   * specifics here (e.g. an explicit connector state, scopes, expiry) that the
   * coarse `status` union can't express. `normalizeSnapshot` passes evidence
   * through verbatim so this survives at runtime; consumers must still treat it
   * as untyped and fail closed on anything unrecognized (discovery ≠ readiness).
   */
  metadata?: Record<string, unknown>;
}

export interface ReadinessSetupAction {
  actionType: string;
  blockerKind: string;
  capabilityId?: string;
  /** Canonical Coherence connection identity when the producer resolved one. */
  connectionId?: string;
  targetRef?: string;
  title: string;
  description?: string;
  dedupeKey: string;
  status?: string;
  contractId?: string;
}

export interface ReadinessSubject {
  type: "org" | "role" | "user";
  roleId?: string;
  roleKey?: string;
  principalId?: string;
}

export interface ReadinessSnapshot {
  /** Stable per-snapshot id from Atlas — the unique selection/key identity. */
  id?: string;
  contractId: string;
  listingId?: string;
  subject: ReadinessSubject;
  subjectKey?: string;
  readinessState: ReadinessState;
  reasonSummary?: string;
  requiredEvidenceKinds: string[];
  satisfiedEvidenceKinds: string[];
  evidence: ReadinessEvidence[];
  setupActions: ReadinessSetupAction[];
  computedAt?: string;
  expiresAt?: string;
  isStale?: boolean;
  metadata?: Record<string, unknown>;
}

/** Fail-closed: unrecognized / missing → `unknown`, NEVER `ready`. */
export function normalizeState(value: unknown): ReadinessState {
  return typeof value === "string" && STATE_SET.has(value as ReadinessState)
    ? (value as ReadinessState)
    : "unknown";
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

/** Normalize a raw snapshot from the API into a guaranteed-shaped object. */
export function normalizeSnapshot(raw: Record<string, unknown>): ReadinessSnapshot {
  const subjectRaw = (raw.subject ?? {}) as Record<string, unknown>;
  const subjectType = subjectRaw.type;
  const evidence = Array.isArray(raw.evidence) ? (raw.evidence as ReadinessEvidence[]) : [];
  const setupActions = Array.isArray(raw.setupActions)
    ? (raw.setupActions as ReadinessSetupAction[])
    : [];
  return {
    id: asString(raw.id),
    contractId: asString(raw.contractId) ?? "",
    listingId: asString(raw.listingId),
    subject: {
      type:
        subjectType === "org" || subjectType === "role" || subjectType === "user"
          ? subjectType
          : "user",
      roleId: asString(subjectRaw.roleId),
      roleKey: asString(subjectRaw.roleKey),
      principalId: asString(subjectRaw.principalId),
    },
    subjectKey: asString(raw.subjectKey),
    readinessState: normalizeState(raw.readinessState),
    reasonSummary: asString(raw.reasonSummary),
    requiredEvidenceKinds: asStringArray(raw.requiredEvidenceKinds),
    satisfiedEvidenceKinds: asStringArray(raw.satisfiedEvidenceKinds),
    evidence,
    setupActions,
    computedAt: asString(raw.computedAt),
    expiresAt: asString(raw.expiresAt),
    isStale: typeof raw.isStale === "boolean" ? raw.isStale : undefined,
    metadata:
      raw.metadata && typeof raw.metadata === "object"
        ? (raw.metadata as Record<string, unknown>)
        : undefined,
  };
}

export interface StateMeta {
  label: string;
  description: string;
  /** Tailwind classes for the badge (distinct per state). */
  badgeClass: string;
  /** Tailwind class for the small status dot. */
  dotClass: string;
}

function stateCopyToMeta(state: ReadinessState): { label: string; description: string } {
  const copy = readinessStateCopy(state);
  return { label: copy.label, description: copy.explanation };
}

// Labels/descriptions source from the SYN-1486 buyer-language layer so copy
// can't drift between surfaces; this table owns only the visual treatment.
export const STATE_META: Record<ReadinessState, StateMeta> = {
  ready: {
    ...stateCopyToMeta("ready"),
    badgeClass:
      "bg-emerald-500/10 text-emerald-600 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-400 dark:border-emerald-900",
    dotClass: "bg-emerald-500",
  },
  needs_setup: {
    ...stateCopyToMeta("needs_setup"),
    badgeClass:
      "bg-amber-500/10 text-amber-600 border-amber-200 dark:bg-amber-500/15 dark:text-amber-400 dark:border-amber-900",
    dotClass: "bg-amber-500",
  },
  blocked: {
    ...stateCopyToMeta("blocked"),
    badgeClass:
      "bg-red-500/10 text-red-600 border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-900",
    dotClass: "bg-red-500",
  },
  stale: {
    ...stateCopyToMeta("stale"),
    badgeClass:
      "bg-sky-500/10 text-sky-600 border-sky-200 dark:bg-sky-500/15 dark:text-sky-400 dark:border-sky-900",
    dotClass: "bg-sky-500",
  },
  unavailable: {
    ...stateCopyToMeta("unavailable"),
    badgeClass:
      "bg-zinc-500/10 text-zinc-600 border-zinc-200 dark:bg-zinc-500/15 dark:text-zinc-400 dark:border-zinc-800",
    dotClass: "bg-zinc-400",
  },
  unknown: {
    ...stateCopyToMeta("unknown"),
    badgeClass:
      "bg-violet-500/10 text-violet-600 border-violet-200 dark:bg-violet-500/15 dark:text-violet-400 dark:border-violet-900",
    dotClass: "bg-violet-400",
  },
};

/** A stale-flagged but otherwise-ready snapshot must display as stale. */
export function effectiveState(snapshot: ReadinessSnapshot): ReadinessState {
  if (snapshot.readinessState === "ready" && snapshot.isStale) {
    return "stale";
  }
  return snapshot.readinessState;
}

function metaString(snapshot: ReadinessSnapshot, key: string): string | undefined {
  const value = snapshot.metadata?.[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export function cardTitle(snapshot: ReadinessSnapshot): string {
  // Contract UUIDs are never display labels (SYN-1486) — when no human name
  // exists the generic noun renders and the raw id stays in Technical details.
  return (
    metaString(snapshot, "title") ??
    metaString(snapshot, "name") ??
    displayName(snapshot.contractId)
  );
}

/** Category / function line — only where data exists. */
export function cardCategory(snapshot: ReadinessSnapshot): string | undefined {
  return (
    metaString(snapshot, "function") ??
    metaString(snapshot, "category") ??
    metaString(snapshot, "sector")
  );
}

export function cardDescription(snapshot: ReadinessSnapshot): string | undefined {
  // reasonSummary arrives as machine codes for some states (e.g.
  // snapshot_past_freshness_horizon) — translate, never echo (SYN-1502).
  return (
    readinessReasonLabel(snapshot.reasonSummary) ??
    metaString(snapshot, "description") ??
    metaString(snapshot, "summary")
  );
}

function connectorRefs(snapshot: ReadinessSnapshot): string[] {
  const refs = new Set<string>();
  for (const e of snapshot.evidence) {
    if (e.kind === "connector" && e.subjectRef) {
      refs.add(e.subjectRef);
    }
  }
  for (const a of snapshot.setupActions) {
    if (a.blockerKind === "connector" && a.targetRef) {
      refs.add(a.targetRef);
    }
  }
  return [...refs];
}

function capabilityRefs(snapshot: ReadinessSnapshot): string[] {
  const refs = new Set<string>();
  for (const e of snapshot.evidence) {
    if (e.capabilityId) {
      refs.add(e.capabilityId);
    }
  }
  for (const a of snapshot.setupActions) {
    if (a.capabilityId) {
      refs.add(a.capabilityId);
    }
  }
  return [...refs];
}

export interface Facets {
  functions: string[];
  capabilities: string[];
  connectors: string[];
  states: ReadinessState[];
}

export function deriveFacets(snapshots: ReadinessSnapshot[]): Facets {
  const functions = new Set<string>();
  const capabilities = new Set<string>();
  const connectors = new Set<string>();
  const states = new Set<ReadinessState>();
  for (const s of snapshots) {
    const fn = cardCategory(s);
    if (fn) {
      functions.add(fn);
    }
    for (const cap of capabilityRefs(s)) {
      capabilities.add(cap);
    }
    for (const conn of connectorRefs(s)) {
      connectors.add(conn);
    }
    states.add(effectiveState(s));
  }
  const sort = (set: Set<string>) => [...set].sort((a, b) => a.localeCompare(b));
  return {
    functions: sort(functions),
    capabilities: sort(capabilities),
    connectors: sort(connectors),
    states: [...states],
  };
}

export interface ReadinessFilters {
  search?: string;
  state?: ReadinessState;
  function?: string;
  capability?: string;
  connector?: string;
}

export function matchesFilters(snapshot: ReadinessSnapshot, filters: ReadinessFilters): boolean {
  if (filters.state && effectiveState(snapshot) !== filters.state) {
    return false;
  }
  if (filters.function && cardCategory(snapshot) !== filters.function) {
    return false;
  }
  if (filters.capability && !capabilityRefs(snapshot).includes(filters.capability)) {
    return false;
  }
  if (filters.connector && !connectorRefs(snapshot).includes(filters.connector)) {
    return false;
  }
  const search = filters.search?.trim().toLowerCase();
  if (search) {
    const haystack = [
      cardTitle(snapshot),
      snapshot.contractId,
      cardCategory(snapshot) ?? "",
      cardDescription(snapshot) ?? "",
    ]
      .join(" ")
      .toLowerCase();
    if (!haystack.includes(search)) {
      return false;
    }
  }
  return true;
}

const UNSATISFIED = new Set(["needs_setup", "blocked", "stale", "unknown"]);

/**
 * Canonical render order for blocker dimensions (SYN-1419 AC3 — "groups blockers
 * by role, connector, grant, registration, canary, drift, and entitlement").
 * Unlisted kinds sort after these, alphabetically.
 */
export const BLOCKER_ORDER: readonly string[] = [
  "role",
  "connector",
  "grant",
  "registration",
  "canary",
  "drift",
  "entitlement",
  "dependency",
  "stale",
] as const;

// Human-readable group labels + the setup-action command mapping live in the
// sibling `readiness-actions.ts` (presentation), keeping this module focused on
// derivation. Render order, below, is intrinsic to grouping so it stays here.

function blockerOrderIndex(kind: string): number {
  const i = BLOCKER_ORDER.indexOf(kind);
  return i === -1 ? BLOCKER_ORDER.length : i;
}

export interface BlockerItem {
  label: string;
  detail?: string;
  status: string;
  source?: string;
}

export interface BlockerGroup {
  kind: string;
  items: BlockerItem[];
  actions: ReadinessSetupAction[];
}

/**
 * Group evidence-backed blockers by dimension. Satisfied evidence is excluded.
 * Groups come back in canonical `BLOCKER_ORDER` so the detail panel reads the
 * same way every time, regardless of the order Atlas emitted evidence/actions.
 */
export function groupBlockers(snapshot: ReadinessSnapshot): BlockerGroup[] {
  const byKind = new Map<string, BlockerGroup>();
  const ensure = (kind: string): BlockerGroup => {
    let group = byKind.get(kind);
    if (!group) {
      group = { kind, items: [], actions: [] };
      byKind.set(kind, group);
    }
    return group;
  };
  for (const e of snapshot.evidence) {
    if (!UNSATISFIED.has(e.status)) {
      continue;
    }
    ensure(e.kind).items.push({
      label: e.requirementKey ?? e.capabilityId ?? e.subjectRef ?? e.kind,
      detail: e.detail,
      status: e.status,
      source: e.source,
    });
  }
  for (const a of snapshot.setupActions) {
    ensure(a.blockerKind).actions.push(a);
  }
  return [...byKind.values()].sort(
    (a, b) => blockerOrderIndex(a.kind) - blockerOrderIndex(b.kind) || a.kind.localeCompare(b.kind),
  );
}

/**
 * Atlas returns setup actions both embedded on snapshots AND via the standalone
 * `/setup-actions` endpoint (each carrying `contractId`). Merge the standalone
 * actions onto the matching snapshot, deduped by `dedupeKey`, so a `needs_setup`
 * card surfaces its action regardless of which shape Atlas used.
 */
export function mergeSetupActions(
  snapshots: ReadinessSnapshot[],
  actions: ReadinessSetupAction[],
): ReadinessSnapshot[] {
  if (actions.length === 0) {
    return snapshots;
  }
  const byContract = new Map<string, ReadinessSetupAction[]>();
  for (const action of actions) {
    if (!action.contractId) {
      continue;
    }
    const list = byContract.get(action.contractId) ?? [];
    list.push(action);
    byContract.set(action.contractId, list);
  }
  if (byContract.size === 0) {
    return snapshots;
  }
  return snapshots.map((snapshot) => {
    const extra = byContract.get(snapshot.contractId);
    if (!extra || extra.length === 0) {
      return snapshot;
    }
    const seen = new Set(snapshot.setupActions.map((a) => a.dedupeKey));
    const fresh = extra.filter((a) => !seen.has(a.dedupeKey));
    if (fresh.length === 0) {
      return snapshot;
    }
    return { ...snapshot, setupActions: [...snapshot.setupActions, ...fresh] };
  });
}

export interface PrimaryAction {
  kind: "open" | "setup" | "view";
  label: string;
  setupAction?: ReadinessSetupAction;
}

/** Open is offered ONLY for a genuinely ready snapshot (discovery ≠ readiness). */
export function primaryAction(snapshot: ReadinessSnapshot): PrimaryAction {
  if (effectiveState(snapshot) === "ready") {
    return { kind: "open", label: "Open" };
  }
  const action = snapshot.setupActions[0];
  if (action) {
    // Buyer CTA from the SYN-1486 layer — Atlas's raw title may carry
    // internal refs and never renders on cards.
    const copy = setupActionCopy(action.actionType, {
      system: systemNameFromRef(action.targetRef),
    });
    return { kind: "setup", label: copy.cta, setupAction: action };
  }
  return { kind: "view", label: "View details" };
}
